import { memo, useMemo, useRef, useDeferredValue } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import * as htmlToImage from "html-to-image";

import Img from "./Img";
import download from "../assets/download.svg";

// ── Constants ────────────────────────────────────────────────────────────────

const PALETTE = [
  "#2563EB",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#14B8A6",
  "#F97316",
  "#22C55E",
  "#E11D48",
  "#0EA5E9",
  "#7C3AED",
  "#84CC16",
  "#F43F5E",
  "#A855F7",
  "#06B6D4",
];

const TOOLTIP_STYLE = {
  backgroundColor: "#1e293b",
  border: "none",
  borderRadius: "12px",
  color: "white",
  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
  fontSize: "14px",
};

// ── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ChartWrapper = styled.div`
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
`;

const ChartContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #e2e8f0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const ChartTitle = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  font-weight: 800;
`;

const DownloadButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function downsample(rows, threshold = 1200) {
  if (!rows || rows.length <= threshold) return rows;
  const step = Math.ceil(rows.length / threshold);
  const out = [];
  for (let i = 0; i < rows.length; i += step) out.push(rows[i]);
  if (out.at(-1) !== rows.at(-1)) out.push(rows.at(-1));
  return out;
}

function buildColorMap(keys) {
  return Object.fromEntries(
    keys.map((k, i) => [k, PALETTE[i % PALETTE.length]]),
  );
}

function transformData(data, selectedSet) {
  const out = [];

  for (const [scenarioKey, items] of Object.entries(data ?? {})) {
    if (!selectedSet.has(scenarioKey)) continue;

    const byName = {};
    for (const { name, value } of items) {
      (byName[name] ??= []).push(value);
    }

    const proteins = Object.keys(byName);
    if (!proteins.length) continue;

    const steps = Math.min(...proteins.map((p) => byName[p].length));
    if (!Number.isFinite(steps)) continue;

    for (let i = 0; i < steps; i++) {
      const point = { name: scenarioKey, x: i + 1 };
      for (const p of proteins) point[p] = byName[p][i];
      out.push(point);
    }
  }

  return out;
}

// ── Component ────────────────────────────────────────────────────────────────

function Plots({ selected, pickedElement, pickedModel, data }) {
  const selectedD = useDeferredValue(selected ?? []);
  const pickedElementD = useDeferredValue(pickedElement ?? []);

  const selectedSet = useMemo(() => new Set(selectedD), [selectedD]);
  const pickedElementSet = useMemo(
    () => new Set(pickedElementD),
    [pickedElementD],
  );

  const chartData = useMemo(
    () => transformData(data, selectedSet),
    [data, selectedSet],
  );

  const filteredEntries = useMemo(() => {
    const grouped = {};
    for (const row of chartData) {
      (grouped[row.name] ??= []).push(row);
    }
    return Object.entries(grouped)
      .filter(([k]) => selectedSet.has(k))
      .map(([scenario, rows]) => [scenario, downsample(rows.slice(0, -1))]);
  }, [chartData, selectedSet]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colorMap = useMemo(
    () => buildColorMap(Array.from(pickedElementSet)),
    [pickedElementD],
  );

  const chartRefs = useRef({});

  const downloadChart = (scenario) => {
    const node = chartRefs.current[scenario];
    if (!node) return;
    htmlToImage
      .toPng(node)
      .then((dataUrl) => {
        Object.assign(document.createElement("a"), {
          download: `Scenario ${Number(scenario.replace(/\D/g, "")) + 1}.png`,
          href: dataUrl,
        }).click();
      })
      .catch((err) => console.error("Error exporting chart:", err));
  };

  return (
    <Container>
      <ChartWrapper>
        {filteredEntries.map(([scenario, rows]) => (
          <ChartContainer
            key={scenario}
            ref={(el) => {
              chartRefs.current[scenario] = el;
            }}
          >
            <DownloadButton
              onClick={() => downloadChart(scenario)}
              aria-label={`Download ${scenario} chart`}
            >
              <Img src={download} />
            </DownloadButton>
            <ChartTitle>
              {`${String(pickedModel)
                .replace(/\.[^/.]+$/i, "")
                .replace(/__fmt\d+$/i, "")
                .toUpperCase()} - Scenario ${Number(scenario.replace(/\D/g, "")) + 1}`}
            </ChartTitle>

            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={rows}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="x"
                  type="number"
                  tickCount={5}
                  domain={[1, (dataMax) => dataMax]}
                  tick={{ fill: "#374151", fontSize: 14 }}
                  label={{
                    value: "Step",
                    position: "insideBottom",
                    offset: -10,
                  }}
                  height={50}
                />
                <YAxis
                  domain={[0, 100]}
                  label={{
                    value: "Activity (%)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ paddingTop: "20px" }} />
                {pickedElementD.map((elKey) => (
                  <Line
                    key={`${scenario}-${elKey}`}
                    type="monotone"
                    dataKey={elKey}
                    stroke={colorMap[elKey] ?? "#2563EB"}
                    strokeWidth={3}
                    dot={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        ))}
      </ChartWrapper>
    </Container>
  );
}

export default memo(Plots);
